import { useApi } from '@src/app/context/Api';
import { useToast } from '@uhg-abyss/web/hooks/useToast';
import { isArray } from 'lodash';
import { useCallback } from 'react';

import reports from './includes/reports.json';

/**
 * Hook: useSaveAlertConfiguration
 *
 * Makes an API request to save the alert configuration
 *
 * @param reportId
 * @returns {{saveEvent: *, handleSave: (function({}=, function()=): Promise<*>)}}
 */
export const useSaveAlertConfiguration = (reportId = null) => {
  const currentReport = reports?.[reportId];

  const { clearApiCache, useApiMutation } = useApi('Alert');
  const [SaveAlertConfiguration, mutation] = useApiMutation('SaveAlertConfiguration');

  const { toast } = useToast();

  const handleSave = useCallback(
    async (variables = {}, refetch = () => {}) => {
      if (!variables?.[reportId] || !isArray(variables[reportId]) || variables[reportId].length === 0) {
        return;
      }

      const toastId = `admin-alert-config-${reportId}`;

      toast.show({
        autoClose: false,
        id: `${toastId}-info`,
        isLoading: true,
        message: currentReport?.info?.message,
        title: currentReport?.info?.title,
        variant: 'info',
      });

      const mutations = [];

      variables?.[reportId]?.forEach((payload = {}) => {
        mutations.push(
          SaveAlertConfiguration(payload, {
            onError: () => {
              toast.hide(`${toastId}-info`);
              toast.show({
                id: `${toastId}-error`,
                message: currentReport?.error?.message,
                title: currentReport?.error?.title,
                variant: 'error',
              });
            },
            onSuccess: async () => {
              toast.hide(`${toastId}-info`);

              toast.show({
                id: `${toastId}-success`,
                message: currentReport?.success?.message,
                title: currentReport?.success?.title,
                variant: 'success',
              });

              await clearApiCache([{ apiKey: 'Alert', queryKeys: ['ListAlertConfigurations'] }]);

              refetch();
            },
          })
        );
      });

      await Promise.all(mutations);
    },
    [currentReport, reportId]
  );

  return { handleSave, saveEvent: mutation };
};
